/**
 * Helpers de formatage partagés par les pages reporting (Phase 12).
 *
 *  - `formatNumber`     : entier/décimal en notation française (séparateur 1 234).
 *  - `formatCurrency`   : montant € (entier).
 *  - `formatDurationH`  : heures décimales → "Xh Ym".
 *  - `formatPercent`    : pourcentage 0..100 → "12,5 %".
 *  - `formatDate`       : ISO → "JJ/MM/AAAA" locale FR.
 *  - `priorityColor`    : code Tailwind bg/text par priorité P0..P7.
 *  - `statutInterventionColor`, `statutEquipementColor` : idem pour les
 *    statuts métier (interventions / équipements).
 */

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('fr-FR').format(value);
}

export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value.toFixed(0)} €`;
  }
}

export function formatDurationH(hoursDecimal: number | null | undefined): string {
  if (hoursDecimal === null || hoursDecimal === undefined || Number.isNaN(hoursDecimal)) return '—';
  if (hoursDecimal === 0) return '0h';
  const h = Math.floor(hoursDecimal);
  const m = Math.round((hoursDecimal - h) * 60);
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

export function formatPercent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value.toFixed(digits)} %`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR');
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR');
}

// ─── Couleurs sémantiques ───────────────────────────────────────────────────

export const PRIORITE_HEX: Record<string, string> = {
  P0: '#b91c1c', // red-700
  P1: '#ef4444', // red-500
  P2: '#f97316', // orange-500
  P3: '#f59e0b', // amber-500
  P4: '#3b82f6', // blue-500
  P5: '#6b7280', // gray-500
  P6: '#6b7280',
  P7: '#6b7280',
};

export const PRIORITE_BG: Record<string, string> = {
  P0: 'bg-red-700',
  P1: 'bg-red-500',
  P2: 'bg-orange-500',
  P3: 'bg-amber-500',
  P4: 'bg-blue-500',
  P5: 'bg-gray-500',
  P6: 'bg-gray-500',
  P7: 'bg-gray-500',
};

export const STATUT_INTERVENTION_HEX: Record<string, string> = {
  nouveau: '#3b82f6',
  en_attente_validation: '#f59e0b',
  affecte: '#6366f1',
  planifie: '#06b6d4',
  en_cours: '#14b8a6',
  en_attente_pieces: '#f97316',
  en_attente_validation_finale: '#a855f7',
  cloture: '#10b981',
  annule: '#6b7280',
};

export const STATUT_INTERVENTION_LABEL: Record<string, string> = {
  nouveau: 'Nouveau',
  en_attente_validation: 'En attente validation',
  affecte: 'Affecté',
  planifie: 'Planifié',
  en_cours: 'En cours',
  en_attente_pieces: 'En attente pièces',
  en_attente_validation_finale: 'En attente validation finale',
  cloture: 'Clôturé',
  annule: 'Annulé',
};

export const STATUT_EQUIPEMENT_HEX: Record<string, string> = {
  en_service: '#10b981',
  en_panne: '#ef4444',
  en_maintenance: '#f97316',
  reforme: '#6b7280',
  en_stock: '#3b82f6',
};

export const STATUT_EQUIPEMENT_LABEL: Record<string, string> = {
  en_service: 'En service',
  en_panne: 'En panne',
  en_maintenance: 'En maintenance',
  reforme: 'Réformé',
  en_stock: 'En stock',
};

export const CRITICITE_HEX: Record<string, string> = {
  vital: '#b91c1c',
  critique: '#f97316',
  important: '#f59e0b',
  standard: '#6b7280',
};

export const CRITICITE_LABEL: Record<string, string> = {
  vital: 'Vital',
  critique: 'Critique',
  important: 'Important',
  standard: 'Standard',
};

export const TYPE_EQUIPEMENT_HEX: Record<string, string> = {
  production: '#3b82f6',
  technique: '#14b8a6',
  informatique: '#a855f7',
  mobilier: '#f59e0b',
};

export const TYPE_EQUIPEMENT_LABEL: Record<string, string> = {
  production: 'Production',
  technique: 'Technique',
  informatique: 'Informatique',
  mobilier: 'Mobilier',
};

export const TYPE_BATIMENT_LABEL: Record<string, string> = {
  principal: 'Principal',
  annexe: 'Annexe',
  technique: 'Technique',
  administratif: 'Administratif',
  operationnel: 'Opérationnel',
};

export const STATUT_BATIMENT_LABEL: Record<string, string> = {
  en_service: 'En service',
  en_travaux: 'En travaux',
  ferme_temporairement: 'Fermé temporairement',
  en_projet: 'En projet',
  desaffecte: 'Désaffecté',
};

export const TYPE_DEMANDE_LABEL: Record<string, string> = {
  panne: 'Panne',
  urgence_critique: 'Urgence critique',
  preventive: 'Préventive',
  amelioration: 'Amélioration',
  installation: 'Installation',
  demenagement: 'Déménagement',
  controle_reglementaire: 'Contrôle réglementaire',
  expertise: 'Expertise',
};
